<template>
    <main class="training-page">
        <section class="training-hero">
            <div>
                <p>Training Lab</p>
                <h1>训练模式</h1>
            </div>
            <button class="btn btn-primary rules-btn" type="button" @click="showRules = true">
                游戏规则
            </button>
        </section>

        <section class="training-grid">
            <button v-for="mode in modes" :key="mode.name" class="training-card" type="button"
                @click="openMode(mode.name)">
                <img :src="mode.image" :alt="mode.title">
                <div>
                    <p>{{ mode.kicker }}</p>
                    <h2>{{ mode.title }}</h2>
                    <span>{{ mode.description }}</span>
                </div>
            </button>
        </section>

        <Teleport to="body">
            <div v-if="showRules" class="rules-overlay" @click.self="showRules = false">
                <section class="rules-dialog" role="dialog" aria-modal="true" aria-labelledby="training-rules-title">
                    <header>
                        <div>
                            <p>Training Rules</p>
                            <h2 id="training-rules-title">训练模式游戏规则</h2>
                        </div>
                        <button type="button" class="close-btn" aria-label="关闭" @click="showRules = false">×</button>
                    </header>
                    <div class="rules-content">
                        <h3>普通贪吃蛇</h3>
                        <ul>
                            <li>使用方向键控制蛇移动，吃到苹果后得分并恢复 HP。</li>
                            <li>撞墙、撞到自己、HP 归零或超过 MaxTurn 时游戏结束。</li>
                        </ul>

                        <h3>单人 AI 训练</h3>
                        <ul>
                            <li>选择一个 AI 策略自动控制蛇移动，观察 Score、Turn 和生存表现。</li>
                            <li>比赛可按三轮参数运行：Apples 1 / MaxHP 100 / MaxTurn 0，Apples 5 / MaxHP 50 / MaxTurn 100，Apples 50 / MaxHP 20 / MaxTurn 200。</li>
                            <li>每轮可记录 AI 死亡前的 Score 和 Turn，用于比较策略稳定性。</li>
                        </ul>

                        <h3>多人 AI 对战</h3>
                        <ul>
                            <li>选择多个 AI 或人类玩家同场对战。</li>
                            <li>蛇头撞墙、撞身体、与其他蛇冲突、HP 归零或超过 MaxTurn 都会判定淘汰。</li>
                            <li>分数更高者表现更优；分数相同时，可比较存活回合数和稳定性。</li>
                        </ul>
                    </div>
                    <footer>
                        <button class="btn btn-primary" type="button" @click="showRules = false">我已知晓</button>
                    </footer>
                </section>
            </div>
        </Teleport>
    </main>
</template>

<script>
import { ref } from 'vue';
import router from '@/router';
import snakeImage from '@/assets/images/匹配.png';
import botImage from '@/assets/images/代码.png';
import battleImage from '@/assets/images/background1.jpg';

export default {
    setup() {
        const showRules = ref(false);
        const modes = [
            {
                name: "single",
                kicker: "Keyboard",
                title: "普通贪吃蛇",
                description: "使用方向键控制蛇移动，适合测试基础地图与操作手感。",
                image: snakeImage,
            },
            {
                name: "ai",
                kicker: "Single AI",
                title: "单人 AI 训练",
                description: "选择 AI 策略自动运行，观察分数、回合数和生存表现。",
                image: botImage,
            },
            {
                name: "battle",
                kicker: "Multi AI",
                title: "多人 AI 对战",
                description: "让多个 AI 同场竞技，用统一规则比较策略优劣。",
                image: battleImage,
            },
        ];

        const openMode = (mode) => {
            router.push({
                name: "training_runner",
                params: { mode },
            });
        };

        return {
            modes,
            openMode,
            showRules,
        };
    },
};
</script>

<style scoped>
.training-page {
    width: min(1120px, calc(100% - 32px));
    margin: 0 auto;
    padding: 28px 0 36px;
}

.training-hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    min-height: 170px;
    margin-bottom: 22px;
    padding: 28px;
    color: var(--app-text);
    background:
        linear-gradient(135deg, rgba(20, 184, 166, 0.16), rgba(37, 99, 235, 0.12)),
        var(--app-surface);
    border: 1px solid var(--app-border);
    border-radius: 18px;
    box-shadow: var(--app-shadow);
}

.training-hero p {
    margin: 0 0 8px;
    color: var(--app-primary);
    font-weight: 900;
}

.training-hero h1 {
    margin: 0 0 10px;
    font-size: 2.2rem;
    font-weight: 900;
}

.training-hero span {
    color: var(--app-muted);
    line-height: 1.8;
}

.rules-btn {
    min-width: 108px;
}

.training-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
}

.training-card {
    overflow: hidden;
    padding: 0;
    text-align: left;
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    border-radius: 16px;
    box-shadow: var(--app-shadow-soft);
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.training-card:hover {
    border-color: rgba(20, 184, 166, 0.44);
    box-shadow: var(--app-shadow);
    transform: translateY(-4px);
}

.training-card img {
    width: 100%;
    height: 170px;
    object-fit: cover;
    background: var(--app-surface-muted);
}

.training-card div {
    padding: 18px;
}

.training-card p {
    margin: 0 0 8px;
    color: var(--app-primary);
    font-size: 0.78rem;
    font-weight: 900;
}

.training-card h2 {
    margin: 0 0 10px;
    color: var(--app-text);
    font-size: 1.2rem;
    font-weight: 900;
}

.training-card span {
    color: var(--app-muted);
    line-height: 1.7;
}

.rules-overlay {
    position: fixed;
    inset: 0;
    z-index: 3200;
    display: grid;
    place-items: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.48);
    backdrop-filter: blur(8px);
}

.rules-dialog {
    width: min(720px, 100%);
    max-height: calc(100vh - 48px);
    overflow: hidden;
    color: var(--app-text);
    background: var(--app-surface);
    border: 1px solid var(--app-border);
    border-radius: 18px;
    box-shadow: var(--app-shadow);
}

.rules-dialog header,
.rules-dialog footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    background: var(--app-surface-muted);
    border-bottom: 1px solid var(--app-border);
}

.rules-dialog footer {
    justify-content: flex-end;
    border-top: 1px solid var(--app-border);
    border-bottom: 0;
}

.rules-dialog header p {
    margin: 0 0 4px;
    color: var(--app-primary);
    font-size: 0.78rem;
    font-weight: 900;
}

.rules-dialog h2 {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 900;
}

.close-btn {
    width: 36px;
    height: 36px;
    color: var(--app-text);
    font-size: 24px;
    line-height: 1;
    background: transparent;
    border: 0;
    border-radius: 10px;
}

.close-btn:hover {
    background: var(--app-surface-strong);
}

.rules-content {
    max-height: min(62vh, 560px);
    overflow-y: auto;
    padding: 22px;
}

.rules-content h3 {
    margin: 0 0 10px;
    color: var(--app-text);
    font-size: 1rem;
    font-weight: 900;
}

.rules-content h3:not(:first-child) {
    margin-top: 20px;
}

.rules-content ul {
    margin: 0;
    padding-left: 20px;
    color: var(--app-muted);
    line-height: 1.8;
}

@media (max-width: 900px) {
    .training-hero {
        align-items: stretch;
        flex-direction: column;
    }

    .rules-btn {
        width: 100%;
    }

    .training-grid {
        grid-template-columns: 1fr;
    }
}
</style>
